import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';

// selectors
import { getActiveVariationOptionValues } from 'cards/reducers/pdp/selectors';
import { getAllDrafts } from 'cards/selectors/draftsSelector';
import { getUserHasIdentified } from 'selectors/user/userSelectors';

// components
import { FavoriteSuiteToggle } from 'favorites/components/FavoriteSuiteToggle';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { CaretIcon, CheckmarkIcon } from '@zola/zola-ui/src/components/SvgIcons';

// utils
import {
  getCardTypeFromSuiteName,
  getShortSuiteName,
} from 'cards/components/CardDetail/Redesign/common/utils';
import { CARD_TYPES } from 'cards/components/CardListing/types';
import { getIsMobileOrTablet } from 'paper/hooks/isMobileOrTablet';

import styles from './styles.module.less';

interface MatchingItemPreviewModalProps {
  activeItemIndex: number;
  authenticated?: string;
  addDraft: (cardSuiteUUID: string, matchingVariationUUID: string, closeModal?: boolean) => void;
  matchingItems: any[];
  recentDrafts: any[];
  removeDraft: (recentDraft: any, modalRecentDrafts?: any[]) => void;
}

const MatchingItemPreviewModal = ({
  activeItemIndex,
  authenticated,
  addDraft,
  matchingItems,
  recentDrafts,
  removeDraft,
}: MatchingItemPreviewModalProps): JSX.Element | null => {
  const [previewIndex, setPreviewIndex] = useState(activeItemIndex);
  const [confirmationHover, setConfirmationHover] = useState(false);
  const [modalRecentDrafts, setModalRecentDrafts] = useState(recentDrafts);

  const isMobileOrTablet = getIsMobileOrTablet();

  const activeVariationOptionValues = useSelector(getActiveVariationOptionValues);
  const existingDrafts = useSelector(getAllDrafts);
  const userHasIdentified = useSelector(getUserHasIdentified);

  // find matching variation
  const activeItem = matchingItems[previewIndex];
  const { colors, suiteName, suiteUUID, type } = activeItem;
  const colorVariation = colors.find(
    ({ value }: { value: string }) => value === activeVariationOptionValues.color
  );
  const matchingVariation = colorVariation || colors[0];

  // find front of card image url
  const image = matchingVariation.plp_front_image_url;

  // check for existing drafts
  const isExistingDraft = !!existingDrafts.find(
    ({ project_name }: { project_name: string }) => project_name === activeItem.suiteName
  );
  const recentDraft = modalRecentDrafts.find(
    ({ name }: { name: string }) => name === activeItem.suiteName
  );

  const handleAddDraft = useCallback(() => {
    if (!matchingVariation?.default_variation_uuid) return null;

    if (authenticated) {
      return (addDraft(suiteUUID, matchingVariation.default_variation_uuid) as any).then(
        (result: any) => {
          setModalRecentDrafts((prevState: any[]) => [...prevState, result]);
        }
      );
    }
    return addDraft(suiteUUID, matchingVariation.default_variation_uuid, true);
  }, [matchingVariation, addDraft, authenticated, suiteUUID]);

  const handleRemoveDraft = useCallback(() => {
    if (!recentDraft) return null;

    const filteredRecentDrafts = modalRecentDrafts.filter(draft => draft.name !== recentDraft.name);

    // pass filteredRecentDrafts when removing a draft in order to update the PDP state
    removeDraft(recentDraft, filteredRecentDrafts);
    return setModalRecentDrafts(filteredRecentDrafts);
  }, [recentDraft, removeDraft, modalRecentDrafts]);

  return (
    <div className={styles.matchingItemPreview}>
      <button
        className={cx(styles.arrow, styles.leftArrow)}
        disabled={previewIndex === 0}
        onClick={() => setPreviewIndex(previewIndex - 1)}
      >
        <CaretIcon className={styles.icon} />
      </button>
      {getShortSuiteName(suiteName)}
      <div className={styles.matchingItemCardType}>
        <div>{getCardTypeFromSuiteName(suiteName)}</div>
        {userHasIdentified && (
          <FavoriteSuiteToggle suiteType={type as CARD_TYPES} suiteUUID={suiteUUID} />
        )}
      </div>
      <div className={styles.matchingItemPreviewImg}>
        <img alt="Matching Item Preview" src={image} />
      </div>
      {recentDraft || isExistingDraft ? (
        <div className={styles.addToDraftsCTA}>
          <div
            className={cx(styles.draftsConfirmation, {
              [styles.draftsConfirmationHover]:
                !isMobileOrTablet && recentDraft && confirmationHover,
            })}
            onMouseEnter={() => setConfirmationHover(true)}
            onMouseLeave={() => setConfirmationHover(false)}
          >
            {!isMobileOrTablet && recentDraft && confirmationHover ? (
              <div className={styles.draftConfirmationText} onClick={handleRemoveDraft}>
                Remove
              </div>
            ) : (
              <div className={styles.draftConfirmationText}>
                <div>
                  <CheckmarkIcon className={styles.checkmark} />
                  {recentDraft ? 'Added to Your Drafts' : 'Already in Your Drafts'}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.addToDraftsCTA} onClick={handleAddDraft}>
          <ButtonV2>Add to Drafts</ButtonV2>
        </div>
      )}
      <button
        className={cx(styles.arrow, styles.rightArrow)}
        disabled={previewIndex === matchingItems.length - 1}
        onClick={() => setPreviewIndex(previewIndex + 1)}
      >
        <CaretIcon className={styles.icon} />
      </button>
    </div>
  );
};

export default MatchingItemPreviewModal;
